import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    FaUserShield, FaUserEdit, FaTrashAlt, FaCheck, FaTimes,
    FaUserTie, FaUserCog, FaTools, FaPlus, FaFilter, FaSearch,
    FaUserPlus, FaPhone, FaBriefcase, FaKey, FaCopy, FaCheckCircle,
    FaTimesCircle, FaGhost, FaUserTag, FaInfoCircle, FaThLarge, FaList, FaCamera, FaSave, FaUsers, FaEnvelope
} from 'react-icons/fa';
import pb from '../../lib/pocketbase';
import styles from '../../pages/Account.module.css';
import { toast } from 'react-toastify';

const TabTeamManagement = ({ adminUser }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [profileFilter, setProfileFilter] = useState('all');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

    // Modal states
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const [updating, setUpdating] = useState(false);
    const [showCredentials, setShowCredentials] = useState(null);

    // Avatar states
    const [avatarFile, setAvatarFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'worker',
        profile_type: 'staff',
        password: '',
        passwordConfirm: ''
    });

    const getProfileTypeInfo = (type) => {
        switch (type) {
            case 'staff': return { bg: '#e0f2fe', color: '#0ea5e9', label: 'Équipe' };
            case 'client': return { bg: '#dbeafe', color: '#2563eb', label: 'Client' };
            case 'candidate': return { bg: '#fef3c7', color: '#d97706', label: 'Candidat' };
            case 'visitor': return { bg: '#f1f5f9', color: '#64748b', label: 'Visiteur' };
            default: return { bg: '#f8fafc', color: '#94a3b8', label: type || 'Inconnu' };
        }
    };

    const getRoleInfo = (role) => {
        switch (role) {
            case 'admin': return { bg: '#fee2e2', color: '#ef4444', label: 'Admin', icon: <FaUserShield /> };
            case 'foreman': return { bg: '#fef3c7', color: '#d97706', label: 'Chef Chantier', icon: <FaUserCog /> };
            case 'worker': return { bg: '#dcfce7', color: '#16a34a', label: 'Terrain', icon: <FaTools /> };
            case 'employee': return { bg: '#e0f2fe', color: '#0ea5e9', label: 'Bureau', icon: <FaBriefcase /> };
            case 'client': return { bg: '#dbeafe', color: '#2563eb', label: 'Client', icon: <FaUserTie /> };
            default: return { bg: '#f1f5f9', color: '#64748b', label: 'Restreint', icon: <FaGhost /> };
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const records = await pb.collection('users').getFullList({
                sort: '-created',
            });
            setUsers(records);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Erreur de synchronisation des accès.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        const unsubscribe = pb.collection('users').subscribe('*', (e) => {
            if (['create', 'update', 'delete'].includes(e.action)) {
                fetchUsers();
            }
        });
        return () => pb.collection('users').unsubscribe('*').catch(() => { });
    }, []);

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch = (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRole = roleFilter === 'all' || user.role === roleFilter;
            const matchesProfile = profileFilter === 'all' || user.profile_type === profileFilter;
            return matchesSearch && matchesRole && matchesProfile;
        });
    }, [users, searchTerm, roleFilter, profileFilter]);

    const handleSelectUser = (id) => {
        setSelectedUsers(prev =>
            prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(u => u.id));
        }
    };

    const handleDeleteBulk = async () => {
        if (!window.confirm(`Supprimer ces ${selectedUsers.length} comptes ?`)) return;
        try {
            setUpdating(true);
            await Promise.all(selectedUsers.map(id => pb.collection('users').delete(id)));
            toast.success("Comptes supprimés.");
            setSelectedUsers([]);
            fetchUsers();
        } catch (error) {
            toast.error("Erreur lors de la suppression groupée.");
        } finally {
            setUpdating(false);
        }
    };

    const handleInlineUpdate = async (userId, field, value) => {
        try {
            await pb.collection('users').update(userId, { [field]: value });
            toast.success("Mise à jour réussie");
            fetchUsers();
        } catch (error) {
            toast.error("Échec de la mise à jour");
        }
    };

    const handleEditClick = (user) => {
        setEditingUser({ ...user });
        setAvatarFile(null);
        setPreviewUrl(user.avatar ? pb.files.getURL(user, user.avatar) : null);
        setIsEditModalOpen(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        try {
            setUpdating(true);
            const data = new FormData();
            data.append('name', editingUser.name);
            data.append('role', editingUser.role);
            data.append('profile_type', editingUser.profile_type);
            if (editingUser.phone) data.append('phone', editingUser.phone);
            if (avatarFile) data.append('avatar', avatarFile);

            await pb.collection('users').update(editingUser.id, data);
            toast.success("Profil mis à jour !");
            setIsEditModalOpen(false);
            fetchUsers();
        } catch (error) {
            toast.error("Erreur de mise à jour");
        } finally {
            setUpdating(false);
        }
    };

    const generatePassword = () => {
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
        let retVal = "";
        for (let i = 0, n = charset.length; i < 10; ++i) {
            retVal += charset.charAt(Math.floor(Math.random() * n));
        }
        setNewUser({ ...newUser, password: retVal, passwordConfirm: retVal });
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (newUser.password !== newUser.passwordConfirm) {
            toast.error("Mots de passe différents");
            return;
        }
        try {
            setUpdating(true);
            await pb.collection('users').create({
                ...newUser,
                emailVisibility: true,
                verified: true
            });
            setShowCredentials({ email: newUser.email, password: newUser.password });
            setNewUser({ name: '', email: '', phone: '', role: 'worker', profile_type: 'staff', password: '', passwordConfirm: '' });
            setIsCreateModalOpen(false);
            fetchUsers();
            toast.success("Compte créé avec succès !");
        } catch (error) {
            toast.error(error.message || "Erreur de création");
        } finally {
            setUpdating(false);
        }
    };

    const handleResetPassword = async (user) => {
        if (!window.confirm(`Envoyer un lien de réinitialisation à ${user.email} ?`)) return;
        try {
            await pb.collection('users').requestPasswordReset(user.email);
            toast.success("Lien envoyé par email.");
        } catch (error) {
            toast.error("Impossible d'envoyer le lien.");
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.info("Copié dans le presse-papier");
    };

    const getAvatarColor = (name) => {
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
        const charCode = (name || 'A').charCodeAt(0);
        return colors[charCode % colors.length];
    };

    return (
        <div className={styles.tabContent}>
            <div className={styles.inventoryHeaderMain}>
                <div className={styles.inventoryTitleGroup}>
                    <h2>
                        <FaUsers /> Accès & Trombinoscope Équipe
                    </h2>
                    <p className={styles.inventorySubtitle}>
                        Pilotez les accès et l'organisation visuelle de la coopérative.
                    </p>
                </div>
                <div className={styles.inventoryActionsGrid}>
                    <div className={styles.planningViewToggle}>
                        <button
                            className={viewMode === 'list' ? styles.activeFilter : styles.filterBtn}
                            onClick={() => setViewMode('list')}
                            title="Vue Liste"
                        >
                            <FaList />
                        </button>
                        <button
                            className={viewMode === 'grid' ? styles.activeFilter : styles.filterBtn}
                            onClick={() => setViewMode('grid')}
                            title="Trombinoscope"
                        >
                            <FaThLarge />
                        </button>
                    </div>
                    <button className={styles.btnAddItem} onClick={() => setIsCreateModalOpen(true)}>
                        <FaUserPlus /> Créer un accès
                    </button>
                </div>
            </div>

            {/* Credentials Display after creation */}
            {showCredentials && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '15px', margin: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ color: '#166534', fontWeight: '800', fontSize: '0.9rem' }}>Compte créé avec succès !</div>
                        <div style={{ fontSize: '0.85rem', color: '#166534' }}>
                            Email: <strong>{showCredentials.email}</strong> | MDP: <strong>{showCredentials.password}</strong>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => copyToClipboard(`Email: ${showCredentials.email}\nPassword: ${showCredentials.password}`)} className={styles.miniActionBtn} style={{ color: '#166534', borderColor: '#bbf7d0' }} title="Copier">
                            <FaCopy />
                        </button>
                        <button onClick={() => setShowCredentials(null)} className={styles.closeBtn} style={{ fontSize: '1.2rem', color: '#166534' }}>×</button>
                    </div>
                </div>
            )}

            {/* Filters Bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '25px', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
                    <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                        className={styles.formInput}
                        placeholder="Rechercher par nom ou email..."
                        style={{ paddingLeft: '45px', width: '100%', borderRadius: '12px', height: '45px' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className={styles.inventoryFiltersModern}>
                    <button
                        onClick={() => setProfileFilter('all')}
                        className={`${styles.filterBtnModern} ${profileFilter === 'all' ? styles.filterBtnModernActive : ''}`}
                    >
                        Tous
                    </button>
                    <button
                        onClick={() => setProfileFilter('staff')}
                        className={`${styles.filterBtnModern} ${profileFilter === 'staff' ? styles.filterBtnModernActive : ''}`}
                    >
                        Équipe
                    </button>
                    <button
                        onClick={() => setProfileFilter('client')}
                        className={`${styles.filterBtnModern} ${profileFilter === 'client' ? styles.filterBtnModernActive : ''}`}
                    >
                        Clients
                    </button>
                </div>

                <select
                    className={styles.formInput}
                    style={{ minWidth: '180px', borderRadius: '12px', height: '45px', width: 'auto' }}
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="all">Tous les rôles</option>
                    <option value="admin">Administrateurs</option>
                    <option value="employee">Bureau & Gestion</option>
                    <option value="worker">Artisans Terrain</option>
                    <option value="client">Espace Client</option>
                    <option value="user">Accès Restreint</option>
                </select>
            </div>

            {/* Bulk Actions */}
            {selectedUsers.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 20px', borderRadius: '12px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', animation: 'fadeIn 0.2s ease' }}>
                    <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '0.9rem' }}>
                        {selectedUsers.length} compte(s) sélectionné(s)
                    </div>
                    <button onClick={handleDeleteBulk} className={styles.submitButton} style={{ background: '#ef4444', height: '36px', fontSize: '0.85rem' }}>
                        <FaTrashAlt style={{ marginRight: '8px' }} /> Supprimer définitivement
                    </button>
                </div>
            )}

            {loading ? (
                <div className={styles.loadingInner}>Synchronisation de la base d'accès...</div>
            ) : (
                <>
                    {viewMode === 'list' ? (
                        <div className={styles.card} style={{ padding: '0', overflow: 'hidden' }}>
                            <div className={styles.tableResponsive}>
                                <table className={styles.appTable}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50px', textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th>Identité / ID</th>
                                            <th>Contact</th>
                                            <th>Catégorie</th>
                                            <th>Rôle Accès</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map(user => (
                                            <tr key={user.id} className={selectedUsers.includes(user.id) ? styles.selectedRow : ''}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.includes(user.id)}
                                                        onChange={() => handleSelectUser(user.id)}
                                                    />
                                                </td>
                                                <td>
                                                    <div className={styles.itemIdentity}>
                                                        {user.avatar ? (
                                                            <div className={styles.itemImageContainer}>
                                                                <img
                                                                    src={pb.files.getURL(user, user.avatar)}
                                                                    alt={user.name}
                                                                    className={styles.itemImage}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className={styles.memberAvatar}
                                                                style={{
                                                                    background: getAvatarColor(user.name),
                                                                    color: 'white',
                                                                    fontWeight: '700',
                                                                    fontSize: '0.9rem',
                                                                    width: '40px', height: '40px'
                                                                }}
                                                            >
                                                                {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div style={{ fontWeight: '700', color: 'var(--color-text)' }}>{user.name || 'Accès Inconnu'}</div>
                                                            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>ID: {user.id}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.85rem' }}>
                                                        <div style={{ color: 'var(--color-primary)', fontWeight: '600' }}>{user.email}</div>
                                                        <div style={{ opacity: 0.6 }}>{user.phone || "Non renseigné"}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <select
                                                        value={user.profile_type}
                                                        onChange={(e) => handleInlineUpdate(user.id, 'profile_type', e.target.value)}
                                                        className={styles.typeBadge}
                                                        style={{
                                                            background: getProfileTypeInfo(user.profile_type).bg,
                                                            color: getProfileTypeInfo(user.profile_type).color,
                                                            border: 'none',
                                                            padding: '5px 12px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '700',
                                                            outline: 'none',
                                                            cursor: 'pointer',
                                                            appearance: 'none',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        <option value="staff">Équipe</option>
                                                        <option value="client">Client</option>
                                                        <option value="candidate">Candidat</option>
                                                        <option value="visitor">Visiteur</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <select
                                                        value={user.role}
                                                        onChange={(e) => handleInlineUpdate(user.id, 'role', e.target.value)}
                                                        className={styles.typeBadge}
                                                        style={{
                                                            background: getRoleInfo(user.role).bg,
                                                            color: getRoleInfo(user.role).color,
                                                            border: 'none',
                                                            padding: '5px 12px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '700',
                                                            outline: 'none',
                                                            cursor: 'pointer',
                                                            appearance: 'none'
                                                        }}
                                                    >
                                                        <option value="admin">Administrateur</option>
                                                        <option value="employee">Bureau & Gestion</option>
                                                        <option value="worker">Terrain</option>
                                                        <option value="client">Espace Client</option>
                                                        <option value="user">Accès Restreint</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.75rem' }}>
                                                        {user.verified ? (
                                                            <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}><FaCheckCircle /> Actif</span>
                                                        ) : (
                                                            <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}><FaTimesCircle /> En attente</span>
                                                        )}
                                                        <div style={{ opacity: 0.5, fontSize: '0.65rem' }}>
                                                            {user.last_login ? `Co: ${new Date(user.last_login).toLocaleDateString()}` : "Jamais connecté"}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => handleEditClick(user)} className={styles.miniActionBtn} title="Modifier le profil">
                                                            <FaUserEdit />
                                                        </button>
                                                        <button onClick={() => handleResetPassword(user)} className={styles.miniActionBtn} title="Réinitialiser MDP">
                                                            <FaKey />
                                                        </button>
                                                        <button
                                                            onClick={() => { if (window.confirm('Supprimer cet accès ?')) pb.collection('users').delete(user.id).then(() => { toast.success('Supprimé'); fetchUsers(); }) }}
                                                            className={styles.miniActionBtn}
                                                            style={{ color: '#ef4444' }}
                                                            title="Supprimer"
                                                        >
                                                            <FaTrashAlt />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                            {filteredUsers.map(user => (
                                <div key={user.id} className={styles.card} style={{ flexDirection: 'column', textAlign: 'center', padding: '25px', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '15px', right: '15px' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.id)}
                                            onChange={() => handleSelectUser(user.id)}
                                        />
                                    </div>

                                    {user.avatar ? (
                                        <div className={styles.itemImageContainer} style={{ width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 15px', border: '3px solid var(--color-light-background)' }}>
                                            <img
                                                src={pb.files.getURL(user, user.avatar)}
                                                alt={user.name}
                                                className={styles.itemImage}
                                                style={{ borderRadius: '50%' }}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            className={styles.memberAvatar}
                                            style={{
                                                background: getAvatarColor(user.name),
                                                color: 'white',
                                                fontWeight: '800',
                                                fontSize: '2rem',
                                                width: '80px', height: '80px',
                                                margin: '0 auto 15px',
                                                border: '3px solid var(--color-light-background)'
                                            }}
                                        >
                                            {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                    )}

                                    <h3 style={{ margin: '0 0 5px', fontSize: '1.2rem', fontWeight: 800 }}>{user.name || "Accès Inconnu"}</h3>

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '15px' }}>
                                        <span style={{
                                            background: getProfileTypeInfo(user.profile_type).bg,
                                            color: getProfileTypeInfo(user.profile_type).color,
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700'
                                        }}>
                                            {getProfileTypeInfo(user.profile_type).label}
                                        </span>
                                        <span style={{
                                            background: getRoleInfo(user.role).bg,
                                            color: getRoleInfo(user.role).color,
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700'
                                        }}>
                                            {getRoleInfo(user.role).label}
                                        </span>
                                    </div>

                                    <div style={{ marginBottom: '20px', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                            <FaEnvelope style={{ opacity: 0.5 }} /> {user.email}
                                        </div>
                                        {user.phone && (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '4px' }}>
                                                <FaPhone style={{ opacity: 0.5 }} /> {user.phone}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: 'auto' }}>
                                        <button onClick={() => handleEditClick(user)} className={styles.btnRefresh} style={{ padding: '8px 15px', fontSize: '0.8rem' }}>Gérer profil</button>
                                        <button onClick={() => handleResetPassword(user)} className={styles.miniActionBtn} title="Réinitialiser MDP">
                                            <FaKey />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {filteredUsers.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '100px 20px', color: '#94a3b8' }}>
                            <FaGhost style={{ fontSize: '3rem', opacity: 0.1, marginBottom: '15px' }} />
                            <p>Aucun membre ne correspond à votre recherche.</p>
                        </div>
                    )}
                </>
            )}

            {/* EDIT MODAL */}
            {isEditModalOpen && editingUser && (
                <div className={styles.modalOverlay} onClick={() => setIsEditModalOpen(false)}>
                    <div className={styles.modalCard} style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Modifier le Profil</h3>
                            <button className={styles.closeBtn} onClick={() => setIsEditModalOpen(false)}>×</button>
                        </div>
                        <form onSubmit={handleSaveEdit} className={styles.modalForm}>
                            {/* Photo Section */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                {previewUrl ? (
                                    <div className={styles.itemImageContainer} style={{ width: '100px', height: '100px', borderRadius: '50%', border: '4px solid var(--color-primary)' }}>
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            className={styles.itemImage}
                                            style={{ borderRadius: '50%' }}
                                        />
                                    </div>
                                ) : (
                                    <div style={{
                                        width: '100px', height: '100px', borderRadius: '50%',
                                        background: getAvatarColor(editingUser.name), color: 'white', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem',
                                        border: '4px solid var(--color-light-background)'
                                    }}>
                                        {editingUser.name ? editingUser.name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                )}
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
                                <button type="button" className={styles.btnRefresh} onClick={() => fileInputRef.current.click()} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaCamera /> Changer la photo
                                </button>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Nom Complet</label>
                                <input
                                    type="text"
                                    className={styles.formInput}
                                    value={editingUser.name || ''}
                                    onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                                />
                            </div>

                            <div className={styles.formGridTwo}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Catégorie</label>
                                    <select className={styles.formInput} value={editingUser.profile_type} onChange={e => setEditingUser({ ...editingUser, profile_type: e.target.value })}>
                                        <option value="staff">Équipe</option>
                                        <option value="client">Client</option>
                                        <option value="candidate">Candidat</option>
                                        <option value="visitor">Visiteur</option>
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Rôle</label>
                                    <select className={styles.formInput} value={editingUser.role} onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}>
                                        <option value="admin">Administrateur</option>
                                        <option value="employee">Bureau & Gestion</option>
                                        <option value="worker">Terrain</option>
                                        <option value="client">Espace Client</option>
                                        <option value="user">Accès Restreint</option>
                                    </select>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Téléphone</label>
                                <input
                                    type="tel"
                                    className={styles.formInput}
                                    value={editingUser.phone || ''}
                                    onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })}
                                />
                            </div>

                            <div style={{ background: 'var(--color-light-background)', padding: '12px', borderRadius: '10px', fontSize: '0.8rem', color: '#64748b', marginBottom: '15px' }}>
                                <strong>ID:</strong> {editingUser.id}<br />
                                <strong>Email:</strong> {editingUser.email}
                            </div>

                            <div className={styles.modalFooter} style={{ gap: '10px', marginTop: '10px', display: 'flex' }}>
                                <button type="button" className={styles.btnRefresh} onClick={() => setIsEditModalOpen(false)} style={{ flex: 1 }}>Annuler</button>
                                <button type="submit" className={styles.btnAddItem} disabled={updating} style={{ flex: 1, justifyContent: 'center' }}>
                                    <FaSave style={{ marginRight: '8px' }} /> {updating ? 'Sauvegarde...' : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* CREATE MODAL */}
            {isCreateModalOpen && (
                <div className={styles.modalOverlay} onClick={() => setIsCreateModalOpen(false)}>
                    <div className={styles.modalCard} style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Créer un nouvel accès</h3>
                            <button className={styles.closeBtn} onClick={() => setIsCreateModalOpen(false)}>×</button>
                        </div>
                        <form onSubmit={handleCreateUser} className={styles.modalForm}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Nom Complet</label>
                                <input
                                    required
                                    type="text"
                                    className={styles.formInput}
                                    value={newUser.name}
                                    onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                                    placeholder="Ex: Jean Dupont"
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Email (Identifiant)</label>
                                <input
                                    required
                                    type="email"
                                    className={styles.formInput}
                                    value={newUser.email}
                                    onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                                    placeholder="email@maisonquinard.fr"
                                />
                            </div>
                            <div className={styles.formGridTwo}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Catégorie</label>
                                    <select className={styles.formInput} value={newUser.profile_type} onChange={e => setNewUser({ ...newUser, profile_type: e.target.value })}>
                                        <option value="staff">Équipe</option>
                                        <option value="client">Client</option>
                                        <option value="candidate">Candidat</option>
                                        <option value="visitor">Visiteur</option>
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Rôle</label>
                                    <select className={styles.formInput} value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                                        <option value="admin">Administrateur</option>
                                        <option value="employee">Bureau & Gestion</option>
                                        <option value="worker">Terrain</option>
                                        <option value="client">Espace Client</option>
                                        <option value="user">Accès Restreint</option>
                                    </select>
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Mot de passe temporaire</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input
                                        required
                                        type="text"
                                        className={styles.formInput}
                                        value={newUser.password}
                                        onChange={e => setNewUser({ ...newUser, password: e.target.value, passwordConfirm: e.target.value })}
                                        placeholder="••••••••"
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        onClick={generatePassword}
                                        className={styles.btnRefresh}
                                        style={{
                                            padding: '0 15px',
                                            borderRadius: '10px',
                                            fontWeight: '700',
                                            fontSize: '0.8rem',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Générer
                                    </button>
                                </div>
                            </div>
                            <button type="submit" disabled={updating} className={styles.btnAddItem} style={{ width: '100%', marginTop: '10px', justifyContent: 'center', height: '50px' }}>
                                {updating ? 'Création...' : 'Créer le compte'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TabTeamManagement;
